import { Edit } from '@mui/icons-material';
import { Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton } from '@mui/material';
import React from 'react';
import { useSelector } from 'react-redux';

import {
  LinearProgressMainLayoutActivate,
  LinearProgressMainLayoutDeactivate,
} from '#app/Action/LinearProgressMainLayoutAction';
import { SnackbarOpen } from '#app/Action/SnackbarAction';
import { TableReload } from '#app/Action/TableAction';
import { dispatch } from '#app/App';
import { hasRight } from '#app/Common/UserCommon';
import ButtonComponent from '#app/Component/ButtonComponent';
import ContentViewComponent from '#app/Component/ContentViewComponent';
import IconAction from '#app/Component/IconAction';
import TableComponent from '#app/Component/TableComponent';
import TextFieldComponent from '#app/Component/TextFieldComponent';
import ApiHandler from '#app/Handler/ApiHandler';
import { initFormHandler } from '#app/Handler/FormHandler';

function SettingProductTypeList() {
  const authenticationReducer = useSelector((state) => state.AuthenticationReducer);
  const [openDialog, setOpenDialog] = React.useState(false);
  const [productType, setProductType] = React.useState(null);

  // Global
  const columns = [
    {
      name: 'id',
      label: 'Référence',
      options: { filter: true, sort: true },
    },
    {
      name: 'label',
      label: 'Libellé',
      options: { filter: true, sort: true },
    },
    {
      name: 'code',
      label: 'Code',
      options: { filter: true, sort: true },
    },
    {
      name: 'action',
      label: 'Action',
      options: { filter: false, sort: false },
    },
  ];
  const getAction = (row) => {
    if (
      hasRight(authenticationReducer, 'ROLE_SETTING_PRODUCT_TYPE') ||
      hasRight(authenticationReducer, 'ROLE_SETTING_PRODUCT_TYPE_EDIT')
    ) {
      return (
        <IconAction
          title="Modifier"
          icon="edit"
          disabled={
            !(
              hasRight(authenticationReducer, 'ROLE_SETTING_PRODUCT_TYPE') ||
              hasRight(authenticationReducer, 'ROLE_SETTING_PRODUCT_TYPE_EDIT')
            )
          }
          onClick={() => {
            handlerForm.reset();
            setProductType(row.id);
            handlerForm.setValue('label', row.label);
            handlerForm.setValue('code', row.code);
            setOpenDialog(true);
          }}
        />
      );
    } else {
      return (
        <IconButton style={{ cursor: 'no-drop' }}>
          <Edit />
        </IconButton>
      );
    }
  };

  // Form
  const [form, setForm] = React.useState({
    label: {
      name: 'label',
      label: 'Libellé',
      textHelper: "Saisissez le libellé du type d'article.",
      type: 'text',
      defaultValue: '',
      options: { validation: ['required'] },
    },
    code: {
      name: 'code',
      label: 'Code',
      textHelper: "Saisissez le code du type d'article.",
      type: 'text',
      defaultValue: '',
      options: { validation: ['required'], equal: 1 },
    },
  });
  const [isLoadingForm, setIsLoadingForm] = React.useState(false);
  const handlerForm = initFormHandler(form, setForm);
  const save = () => {
    if (handlerForm.checkError() < 1) {
      handlerForm.setFormLoading(true);
      setIsLoadingForm(true);
      dispatch(LinearProgressMainLayoutActivate());

      if (productType) {
        ApiHandler.post(
          {
            route: 'api_setting_product_type_edit',
            data: handlerForm.getData(),
            params: { id: productType },
          },
          (response) => {
            if (response.status === 200) {
              setOpenDialog(false);
              dispatch(TableReload('api_setting_product_type_list'));
              setProductType(null);
              dispatch(
                SnackbarOpen({
                  text: "Type d'article modifié avec succès.",
                  variant: 'success',
                }),
              );

              ApiHandler.clearCache('api_select_product_type');
            } else if (response.status === 400) {
              handlerForm.setErrorApi(response.error);
            } else {
              dispatch(
                SnackbarOpen({
                  text: response?.error?.message || "Une erreur inattendue s'est produite.",
                  variant: 'error',
                }),
              );
            }

            dispatch(LinearProgressMainLayoutDeactivate());
            handlerForm.setFormLoading(false);
            setIsLoadingForm(false);
          },
        );
      } else {
        ApiHandler.post(
          {
            route: 'api_setting_product_type_add',
            data: handlerForm.getData(),
          },
          (response) => {
            if (response.status === 200) {
              setOpenDialog(false);
              dispatch(TableReload('api_setting_product_type_list'));
              dispatch(
                SnackbarOpen({
                  text: "Type d'article créé avec succès.",
                  variant: 'success',
                }),
              );

              ApiHandler.clearCache('api_select_product_type');
            } else if (response.status === 400) {
              handlerForm.setErrorApi(response.error);
            } else {
              dispatch(
                SnackbarOpen({
                  text: response?.error?.message || "Une erreur inattendue s'est produite.",
                  variant: 'error',
                }),
              );
            }

            dispatch(LinearProgressMainLayoutDeactivate());
            handlerForm.setFormLoading(false);
            setIsLoadingForm(false);
          },
        );
      }
    }
  };

  React.useEffect(() => {
    handlerForm.start();
  }, []);

  return (
    <ContentViewComponent breadcrumbs={{ title: "Liste des types d'article", context: 'Paramétrage' }}>
      <TableComponent
        id={'api_setting_product_type_list'}
        title={"Types d'article"}
        columns={columns}
        actionFirst={
          hasRight(authenticationReducer, 'ROLE_SETTING_PRODUCT_TYPE') ||
          hasRight(authenticationReducer, 'ROLE_SETTING_PRODUCT_TYPE_ADD')
            ? {
                label: "Ajouter un type d'article",
                onClick: () => {
                  setProductType(null);
                  handlerForm.reset();
                  setOpenDialog(true);
                },
              }
            : false
        }
        promiseData={(resolve) => {
          ApiHandler.get({ route: 'api_setting_product_type_list' }, (response) => {
            const data = response.data;
            for (const index in data) {
              data[index].action = getAction(data[index]);
            }
            resolve(data);
          });
        }}
      />

      <Dialog open={openDialog} maxWidth={'xl'} onClose={() => setOpenDialog(false)}>
        <DialogTitle style={{ fontSize: 15 }}>
          {productType ? "Modifier un type d'article" : "Ajouter un type d'article"}
        </DialogTitle>
        <DialogContent style={{ minWidth: '30vw' }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextFieldComponent id={'label'} handler={handlerForm} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextFieldComponent id={'code'} handler={handlerForm} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <ButtonComponent
            color={'#5E6E82'}
            label={'Annuler'}
            onClick={() => setOpenDialog(false)}
            disabled={isLoadingForm}
          />
          <ButtonComponent label={'Enregistrer'} onClick={save} loading={isLoadingForm} />
        </DialogActions>
      </Dialog>
    </ContentViewComponent>
  );
}

export default SettingProductTypeList;
